import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format, subDays, startOfMonth, startOfYear, startOfDay } from 'date-fns';

/**
 * Range picker with a few quick "presets" (Last 7 / 30 / 90 / Year / All)
 * AND a calendar that picks an arbitrary {from, to}. Talks to the parent only
 * via `onChange({ start_date, end_date, preset })` so the Analytics page can
 * fall back to its old `period` enum when a preset is picked.
 *
 * Why both? Presets are still the fastest path for 80% of cases — and the
 * backend's prev-period delta math is most reliable when running off the
 * canonical `period` enum (week / month / quarter / year / all).
 */

const PRESETS = [
  { key: 'week', label: 'Last 7 Days' },
  { key: 'month', label: 'Last 30 Days' },
  { key: 'quarter', label: 'Last 90 Days' },
  { key: 'year', label: 'Last Year' },
  { key: 'all', label: 'All Time' },
];

const presetToRange = (preset) => {
  const today = startOfDay(new Date());
  switch (preset) {
    case 'week':
      return { from: subDays(today, 6), to: today };
    case 'month':
      return { from: subDays(today, 29), to: today };
    case 'quarter':
      return { from: subDays(today, 89), to: today };
    case 'year':
      return { from: subDays(today, 364), to: today };
    case 'mtd':
      return { from: startOfMonth(today), to: today };
    case 'ytd':
      return { from: startOfYear(today), to: today };
    default:
      return null;
  }
};

const fmtChip = (range) => {
  if (!range || !range.from) return null;
  if (!range.to) return format(range.from, 'd MMM yyyy');
  if (range.from.getTime() === range.to.getTime()) return format(range.from, 'd MMM yyyy');
  return `${format(range.from, 'd MMM')} – ${format(range.to, 'd MMM yyyy')}`;
};

const DateRangePicker = ({ preset, customRange, onChange, testid = 'date-range-picker' }) => {
  // `customRange` (if set) takes precedence; otherwise we render the preset label.
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(customRange || presetToRange(preset || 'month'));

  // Sync draft whenever the parent flips between preset and custom externally
  useEffect(() => {
    setDraft(customRange || presetToRange(preset || 'month'));
  }, [customRange, preset]);

  const labelText = customRange
    ? fmtChip(customRange)
    : (PRESETS.find((p) => p.key === preset)?.label || 'Select range');

  const applyPreset = (key) => {
    onChange({ preset: key, customRange: null });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draft || !draft.from) return;
    const to = draft.to || draft.from;
    onChange({
      preset: null,
      customRange: { from: draft.from, to },
      // ISO yyyy-MM-dd is what the backend accepts for date-only bounds
      start_date: format(draft.from, 'yyyy-MM-dd'),
      end_date: format(to, 'yyyy-MM-dd'),
    });
    setOpen(false);
  };

  const clearCustom = (e) => {
    e.stopPropagation();
    onChange({ preset: 'month', customRange: null });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          data-testid={`${testid}-trigger`}
        >
          <CalendarIcon className="w-4 h-4 text-slate-500" />
          <span className="text-sm">{labelText}</span>
          {customRange ? (
            <span
              role="button"
              tabIndex={0}
              onClick={clearCustom}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') clearCustom(e);
              }}
              className="ml-1 -mr-1 p-0.5 rounded hover:bg-slate-200 transition cursor-pointer inline-flex"
              data-testid={`${testid}-clear`}
              title="Clear custom range — back to Last 30 Days"
              aria-label="Clear custom range"
            >
              <X className="w-3.5 h-3.5 text-slate-500" />
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end" data-testid={`${testid}-content`}>
        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] divide-x divide-slate-100">
          {/* Left rail: presets */}
          <div className="p-2 space-y-1 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold px-2 pt-1 pb-1.5">
              Quick ranges
            </div>
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p.key)}
                data-testid={`${testid}-preset-${p.key}`}
                className={`w-full text-left text-xs px-2 py-1.5 rounded transition ${
                  preset === p.key && !customRange
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-700 hover:bg-white'
                }`}
              >
                {p.label}
              </button>
            ))}
            <div className="h-px bg-slate-200 my-1.5" />
            <button
              type="button"
              onClick={() => applyPreset('mtd')}
              data-testid={`${testid}-preset-mtd`}
              className="w-full text-left text-xs px-2 py-1.5 rounded text-slate-700 hover:bg-white"
            >
              Month-to-date
            </button>
            <button
              type="button"
              onClick={() => applyPreset('ytd')}
              data-testid={`${testid}-preset-ytd`}
              className="w-full text-left text-xs px-2 py-1.5 rounded text-slate-700 hover:bg-white"
            >
              Year-to-date
            </button>
          </div>

          {/* Right pane: calendar + apply */}
          <div className="p-2 space-y-2">
            <Calendar
              mode="range"
              selected={draft}
              onSelect={setDraft}
              numberOfMonths={2}
              defaultMonth={draft?.from}
              data-testid={`${testid}-calendar`}
              disabled={{ after: new Date() }}
            />
            <div className="flex items-center justify-between gap-2 px-1 pb-1">
              <span className="text-[11px] text-slate-500 truncate">
                {draft?.from && draft?.to
                  ? `${format(draft.from, 'd MMM yyyy')} – ${format(draft.to, 'd MMM yyyy')}`
                  : draft?.from
                  ? `From ${format(draft.from, 'd MMM yyyy')} — pick end date`
                  : 'Pick a start and end date'}
              </span>
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid={`${testid}-cancel`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={applyCustom}
                  disabled={!draft?.from}
                  data-testid={`${testid}-apply`}
                  className="bg-teal-700 hover:bg-teal-800"
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
